import {useMemo} from 'react'
import {graphql, usePaginationFragment} from 'react-relay'

import type {IssueTimelineItemsItemType} from '../__generated__/IssueViewerViewQuery.graphql'
import type {useActivityTimelineItemsBackFragment$key} from './__generated__/useActivityTimelineItemsBackFragment.graphql'
import type {useActivityTimelineItemsBackPaginationQuery} from './__generated__/useActivityTimelineItemsBackPaginationQuery.graphql'
import type {useActivityTimelineItemsFrontFragment$key} from './__generated__/useActivityTimelineItemsFrontFragment.graphql'
import type {useActivityTimelineItemsFrontPaginationQuery} from './__generated__/useActivityTimelineItemsFrontPaginationQuery.graphql'
import {createBackFetcherConfig, useTimelineItemsCore, type UseTimelineItemsCoreResult} from './use-timeline-items-core'

// ─── Fragments ──────────────────────────────────────────────────────────────

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const activityFrontFragment = graphql`
  fragment useActivityTimelineItemsFrontFragment on Issue
  @argumentDefinitions(
    count: {type: "Int", defaultValue: 15}
    cursor: {type: "String"}
    itemTypes: {type: "[IssueTimelineItemsItemType!]"}
  )
  @refetchable(queryName: "useActivityTimelineItemsFrontPaginationQuery") {
    activityFrontItems: timelineItems(first: $count, after: $cursor, itemTypes: $itemTypes)
      @defer(label: "Issue__activityFrontItems")
      @connection(key: "Issue__activityFrontItems") {
      pageInfo {
        hasNextPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const activityBackFragment = graphql`
  fragment useActivityTimelineItemsBackFragment on Issue
  @argumentDefinitions(
    count: {type: "Int", defaultValue: 0}
    cursor: {type: "String"}
    itemTypes: {type: "[IssueTimelineItemsItemType!]"}
  )
  @refetchable(queryName: "useActivityTimelineItemsBackPaginationQuery") {
    activityBackItems: timelineItems(last: $count, before: $cursor, itemTypes: $itemTypes)
      @defer(label: "Issue__activityBackItems")
      @connection(key: "Issue__activityBackItems") {
      pageInfo {
        hasPreviousPage
      }
      totalCount
      edges {
        node {
          __id
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...IssueTimelineItem
        }
      }
    }
  }
`

// Custom query for loading more back items.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const activityBackQuery = graphql`
  query useActivityTimelineItemsBackQuery(
    $id: ID!
    $count: Int!
    $cursor: String
    $itemTypes: [IssueTimelineItemsItemType!]
  ) {
    node(id: $id) {
      ... on Issue {
        activityBackItems: timelineItems(last: $count, before: $cursor, itemTypes: $itemTypes) {
          pageInfo {
            startCursor
          }
          totalCount
          edges {
            node {
              __id
              # eslint-disable-next-line relay/must-colocate-fragment-spreads
              ...IssueTimelineItem
            }
          }
        }
      }
    }
  }
`

/**
 * Standalone query for lazy-loading the activity timeline.
 *
 * Fetched on-demand when the Activity tab is first opened, keeping it out of
 * the initial page load.
 */
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const activityTimelineQuery = graphql`
  query useActivityTimelineItemsQuery($issueId: ID!, $activityItemTypes: [IssueTimelineItemsItemType!]) {
    node(id: $issueId) {
      ... on Issue {
        ...useActivityTimelineItemsFrontFragment @arguments(itemTypes: $activityItemTypes)
        ...useActivityTimelineItemsBackFragment @arguments(itemTypes: $activityItemTypes)
      }
    }
  }
`

// ─── Hook ───────────────────────────────────────────────────────────────────

type UseActivityTimelineItemsProps = {
  frontRef: useActivityTimelineItemsFrontFragment$key
  backRef: useActivityTimelineItemsBackFragment$key
  issueId: string
  itemTypes: IssueTimelineItemsItemType[]
  highlightedEvent?: string | undefined
  page?: number
}

/**
 * Hook for fetching and paginating activity timeline items.
 *
 * Structurally identical to `useConversationTimelineItems` but uses its own
 * connection keys (`Issue__activityFrontItems` / `Issue__activityBackItems`)
 * so the two tabs can maintain independent pagination state.
 */
export function useActivityTimelineItems({
  frontRef,
  backRef,
  issueId,
  itemTypes,
  highlightedEvent,
  page,
}: UseActivityTimelineItemsProps): UseTimelineItemsCoreResult {
  const {
    data: {activityFrontItems: frontData},
    loadNext: loadMoreFrontItems,
    hasNext: frontHasNextPage,
  } = usePaginationFragment<useActivityTimelineItemsFrontPaginationQuery, useActivityTimelineItemsFrontFragment$key>(
    activityFrontFragment,
    frontRef,
  )

  const {
    data: {activityBackItems: backData},
  } = usePaginationFragment<useActivityTimelineItemsBackPaginationQuery, useActivityTimelineItemsBackFragment$key>(
    activityBackFragment,
    backRef,
  )

  const backFetcherConfig = useMemo(
    () => ({
      ...createBackFetcherConfig(activityBackQuery, 'activityBackItems'),
      // Override makeVariables to include itemTypes
      makeVariables: (id: string, count: number, cursor: string | null) => ({
        id,
        count,
        cursor,
        itemTypes,
      }),
    }),
    [itemTypes],
  )

  return useTimelineItemsCore({
    issueId,
    frontData,
    backData,
    loadMoreFrontItems,
    backFetcherConfig,
    frontHasNextPage,
    highlightedEvent,
    page,
  })
}
